import { Component, ElementRef, ViewChild, AfterViewInit, Renderer2, OnDestroy, OnInit, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SuiteTechnologiesComponent } from '../../../general/suite-technologies/suite-technologies.component';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';
import { ExploreMoreServicesComponent } from '../explore-more-services/explore-more-services.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface TimelineCard {
  id: number;
  year: string;
  title: string;
  description: string;
  image: string;
}

@Component({
  selector: 'app-ai-as-a-service',
  imports: [CommonModule, SuiteTechnologiesComponent, FaqComponent, ExploreMoreServicesComponent],
  standalone: true,
  templateUrl: './ai-as-a-service.component.html',
  styleUrl: './ai-as-a-service.component.scss'
})
export class AiAsAServiceComponent implements AfterViewInit, OnDestroy, OnInit {
  private jsonLdScript: any;
  @ViewChild('featuresSection') featuresSection!: ElementRef;
  @ViewChild('whyChooseSection') whyChooseSection!: ElementRef;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  isFeaturesVisible = false;
  isWhyChooseVisible = false;
  private observer: IntersectionObserver | null = null;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _renderer: Renderer2,
    private modalService: NgbModal
  ) { }
  goToCaseStudies(): void {

  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }
  aiaasFaqs: FaqItem[] = [
    {
      question: 'What is AI as a Service (AIaaS)?',
      answer: `AIaaS provides ready-to-use AI capabilities via cloud platforms, allowing businesses 
      to access machine learning, NLP, and automation tools without managing the infrastructure.`
    },
    {
      question: 'How can AIaaS help my business?',
      answer: `AIaaS accelerates innovation, automates repetitive tasks, improves customer experience, 
      and enables data-driven decisions, all while keeping operational costs predictable.`
    },
    {
      question: 'Is AIaaS suitable for small and mid-sized businesses?',
      answer: `Absolutely. AIaaS offers flexible pricing and scalable solutions, making it ideal for 
      SMBs looking to implement AI without heavy investments.`
    },
    {
      question: 'Which AIaaS platforms do you use?',
      answer: `We primarily use Microsoft Azure AI, Cognitive Services, and Python-based 
      ML frameworks to ensure performance and security.`
    },
    {
      question: 'How secure is your AIaaS solution?',
      answer: `As a SOC II certified company, Kalpita ensures complete data protection, encryption, 
      and compliance with global privacy standards.`
    }
  ];

  @ViewChild('benefitCardsContainer', { static: false }) private benefitCardsContainer!: ElementRef;

  timelinecards: TimelineCard[] = [
    {
      id: 1,
      year: '2020',
      title: 'AI-Powered Recruitment Transformation for Smarter Hiring',
      description: `Kalpita Technologies replaced manual, Excel-based hiring with an AI-driven recruitment system. The solution automated workflows, centralized candidate data, and delivered real-time analytics through Azure-based AI tools. With RAG chatbot integration, recruiters gained instant insights, faster decisions, and a smarter, data-driven hiring experience.`,
      image: '../../../../assets/services/ai-as-service/successStory-img.png'
    }
    //  {
    //    id: 2,
    //    year: '2023',
    //    title: 'Modernizing US Retirement Plan Analytics ',
    //    description: 'Our team developed a scalable analytics system for a US-based financial firm, automating intricate reporting processes and reducing manual workloads. With this solution, advisors gained actionable insights and delivered timely, error-free client reporting.',
    //    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop'
    //  },
    //  {
    //    id: 3,
    //    year: '2025',
    //    title: 'AI-Powered Insurance Application',
    //    description: 'Harnessing the latest in AI and cloud, Kalpita engineered an insurance application that automates claims processing and fraud detection. The application slashed processing times, improved accuracy, and delivered outstanding customer experiences.',
    //    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=300&fit=crop'
    //  }
    // {
    //   id: 4,
    //   year: '2026',
    //   title: 'Future Vision',
    //   description: 'Leading the way in digital transformation and AI-powered solutions.',
    //   image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop'
    // },
    // {
    //   id: 5,
    //   year: '2027',
    //   title: 'Global Reach',
    //   description: 'Expanding our presence globally with cutting-edge technology solutions.',
    //   image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=400&h=300&fit=crop'
    // }
  ];

  selectedIndex = 0;

  get visibleCards(): Array<{ card: TimelineCard, position: 'top' | 'center' | 'bottom' }> {
    const result = [];
    const totalCards = this.timelinecards.length;

    if (totalCards === 0) return [];

    // Case 1: Single Card
    if (totalCards === 1) {
      result.push({
        card: this.timelinecards[0],
        position: 'center' as const
      });
      return result;
    }

    // Case 2: Multiple Cards (Standard Logic)
    // Top card (only if exists and not the first one)
    if (this.selectedIndex > 0) {
      result.push({
        card: this.timelinecards[this.selectedIndex - 1],
        position: 'top' as const
      });
    }

    // Center card
    result.push({
      card: this.timelinecards[this.selectedIndex],
      position: 'center' as const
    });

    // Bottom card (only if exists and not the last one)
    if (this.selectedIndex < totalCards - 1) {
      result.push({
        card: this.timelinecards[this.selectedIndex + 1],
        position: 'bottom' as const
      });
    }

    return result;
  }

  get showCarouselDots(): boolean {
    return this.timelinecards.length > 3;
  }

  selectCard(position: 'top' | 'center' | 'bottom'): void {
    if (this.timelinecards.length <= 1) return; // Do nothing if single card

    if (position === 'top' && this.selectedIndex > 0) {
      this.selectedIndex--;
    } else if (position === 'bottom' && this.selectedIndex < this.timelinecards.length - 1) {
      this.selectedIndex++;
    }
  }

  goToCard(index: number): void {
    if (index >= 0 && index < this.timelinecards.length) {
      this.selectedIndex = index;
    }
  }

  get canScrollUp(): boolean {
    return this.selectedIndex > 0;
  }

  get canScrollDown(): boolean {
    return this.selectedIndex < this.timelinecards.length - 1;
  }

  whyChooseFeatures = [
    {
      icon: '../../../../assets/services/ai-as-service/whyChooseUs/End-to-EndAIExpertise.png',
      title: 'End-to-End AI Expertise ',
      description: 'From roadmap design to post-deployment optimization.'
    },
    {
      icon: '../../../../assets/services/ai-as-service/whyChooseUs/FlexibleAIaaSModels.png',
      title: 'Flexible AIaaS Models ',
      description: 'Choose from managed services, hybrid setups, or full-scale enterprise AI solutions.'
    },
    {
      icon: '../../../../assets/services/ai-as-service/whyChooseUs/Cross-PlatformIntegration.png',
      title: 'Cross-Platform Integration ',
      description: 'AI for web, mobile, and data applications built with React, Python, and Azure.'
    },
    {
      icon: '../../../../assets/services/ai-as-service/whyChooseUs/CommitmentToDataSecurity.png',
      title: 'Commitment to Data Security ',
      description: 'SOC II certified and compliant with enterprise-grade data governance.'
    }
  ];

  developmentProcess = [
    {
      icon: '../../../../assets/services/ai-as-service/developmentProcess/AIStrategyRoadmap.png',
      title: 'AI Strategy & Roadmap ',
      description: 'Identify high-value AI opportunities aligned with your business objectives.'
    },
    {
      icon: '../../../../assets/services/ai-as-service/developmentProcess/DataPreparationModelDevelopment.png',
      title: 'Data Preparation & Model Development',
      description: 'Build and train custom ML models using Azure ML and Python.'
    },
    {
      icon: '../../../../assets/services/ai-as-service/developmentProcess/CloudIntegrationDeployment.png',
      title: 'Cloud Integration & Deployment',
      description: 'Host scalable models on Microsoft Azure for enterprise-grade performance.'
    },
    {
      icon: '../../../../assets/services/ai-as-service/developmentProcess/ApplicationIntegration.png',
      title: 'Application Integration',
      description: 'Embed AI seamlessly into your web, mobile, or analytics dashboards using React or native app frameworks.'
    },
    {
      icon: '../../../../assets/services/ai-as-service/developmentProcess/ContinuousOptimization.png',
      title: 'Continuous Optimization',
      description: 'Monitor and refine models with performance analytics and real-time feedback loops.'
    }
  ];

  // Service Overview section data and logic
  services = [
    {
      title: 'Faster Time to Market',
      description: 'We design software that grows with your business and adapts to new technologies.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Secure & Compliant',
      description: 'Our agile development process ensures quick and efficient delivery of your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Innovation at Core',
      description: 'We build robust and secure software that complies with industry standards.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Future-Ready Architecture',
      description: 'We leverage the latest technologies to build innovative and cutting-edge software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'End to End Support',
      description: 'We provide comprehensive support and maintenance services for your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    }
  ];
  selectedServiceIndex = 3; // Default to "Future-Ready Architecture"

  // Pre-defined positions for the icons on the arc and the active position
  private arcPositions = [
    { x: -180, y: 80 },
    { x: -110, y: -20 },
    { x: 0, y: -60 },
    { x: 110, y: -20 },
    { x: 180, y: 80 }
  ];
  private activePosition = { x: 0, y: 150 };

  selectService(index: number): void {
    this.selectedServiceIndex = index;
  }

  getPointTransform(index: number): string {
    let pos;
    if (index === this.selectedServiceIndex) {
      pos = this.activePosition;
    } else {
      pos = this.arcPositions[index];
    }
    return `translate(${pos.x}px, ${pos.y}px)`;
  }

  readMore(): void {
  }
  setupTiltEffect(): void {
    const cards = document.querySelectorAll('.feature-card, .features-img');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate center
        const xc = rect.width / 2;
        const yc = rect.height / 2;

        // Max rotation degrees
        const maxRotate = 10;

        // Calculate rotation based on cursor position
        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate; // Inverse Y axis

        // Apply transform
        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        // Reset to flat state
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        // Smooth entry
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      // Attach Listeners
      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      // Optional: Disable on touch devices for better scrolling
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  setupScrollAnimation(): void {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {

          if (this.featuresSection && entry.target === this.featuresSection.nativeElement) {
            this.isFeaturesVisible = true;
            observer.unobserve(entry.target);
          }

          if (this.whyChooseSection && entry.target === this.whyChooseSection.nativeElement) {
            this.isWhyChooseVisible = true;
            observer.unobserve(entry.target);
          }
        }
      });
    }, {
      threshold: 0.2 // Trigger when 20% visible
    });

    if (this.featuresSection) {
      observer.observe(this.featuresSection.nativeElement);
    }

    if (this.whyChooseSection) {
      observer.observe(this.whyChooseSection.nativeElement);
    }

    this.observer = observer;
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    if (this.observer) {
      this.observer.disconnect();
    }
  }
  injectSchemaMarkup(): void {
    const aiAsAServiceSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/ai-as-a-service#webpage",
          "url": "https://www.kalpitatechnologies.com/company-services/ai-as-a-service",
          "name": "AI as a Service (AIaaS) | Machine Learning & AI Solutions | Kalpita Technologies",
          "description": "Enterprise AI as a Service solutions on Microsoft Azure. Custom machine learning, NLP chatbots, computer vision, and intelligent automation.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en-US"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/company-services/ai-as-a-service#breadcrumbs",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.kalpitatechnologies.com/company-services" },
            { "@type": "ListItem", "position": 3, "name": "AI as a Service", "item": "https://www.kalpitatechnologies.com/company-services/ai-as-a-service" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/company-services/ai-as-a-service#service",
          "name": "AI as a Service (AIaaS)",
          "serviceType": "AI as a Service",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "Enterprise AI as a Service solutions leveraging Microsoft Azure AI, Python ML libraries, and cloud-native architecture."
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/company-services/ai-as-a-service#process",
          "name": "Kalpita's AIaaS Process",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "AI Strategy & Roadmap", "text": "Identify high-value AI opportunities aligned with your objectives." },
            { "@type": "HowToStep", "position": 2, "name": "Data Preparation", "text": "Build and train custom ML models using Azure ML and Python." },
            { "@type": "HowToStep", "position": 3, "name": "Cloud Deployment", "text": "Host scalable models on Microsoft Azure for performance." },
            { "@type": "HowToStep", "position": 4, "name": "Application Integration", "text": "Embed AI into web, mobile, or analytics dashboards." },
            { "@type": "HowToStep", "position": 5, "name": "Continuous Optimization", "text": "Monitor and refine models with performance analytics." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/ai-as-a-service#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is AI as a Service (AIaaS)?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "AIaaS provides ready-to-use AI capabilities via cloud platforms, allowing businesses to access ML, NLP, and automation tools without managing infrastructure."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this._renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(aiAsAServiceSchema);
    this._renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this._renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    this.setupTiltEffect();
    this.setupScrollAnimation();
    const cards = document.querySelectorAll('.feature-card, .features-img');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate center
        const xc = rect.width / 2;
        const yc = rect.height / 2;

        // Max rotation degrees
        const maxRotate = 10;

        // Calculate rotation based on cursor position
        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate; // Inverse Y axis

        // Apply transform
        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        // Reset to flat state
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        // Smooth entry
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      // Attach Listeners
      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      // Optional: Disable on touch devices for better scrolling
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  getAFreeQuote(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  bookAFreeCall(): void {
    this.getAFreeQuote();
  }
}
